import { useCallback, useState, FocusEvent, ReactNode } from 'react';

// components
import { Box, Grid, InputLabel, makeStyles, useTheme } from '@material-ui/core';
import MentionsInputBase, { MentionsInputBaseProps } from './MentionsInputBase';
import InfoSection, { isOverCharacterLimit } from './InfoSection';

// styles
import { Theme } from 'assets/theme';

export type MentionsTextFieldProps = MentionsInputBaseProps & {
  label: string;
  maxLength?: number;
  /** Rendered next to the input in the inline layout (e.g. a submit button). */
  endAdornment?: ReactNode;
  /** Applied to the row wrapping the input in the inline layout. */
  containerClassName?: string;
};

export default function MentionsTextField({
  label,
  maxLength,
  value,
  onFocus,
  onBlur,
  multiline = false,
  endAdornment,
  containerClassName,
  ...rest
}: MentionsTextFieldProps) {
  const classes = useStyles();
  const theme = useTheme<Theme>();
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const isOverLimit = isOverCharacterLimit(value, maxLength);

  const handleFocus = useCallback(
    (event: FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(true);
      onFocus && onFocus(event);
    },
    [onFocus]
  );

  const handleBlur = useCallback(
    (
      event: FocusEvent<HTMLInputElement> | FocusEvent<HTMLTextAreaElement>,
      clickedSuggestion: boolean
    ) => {
      setIsFocused(false);
      onBlur && onBlur(event, clickedSuggestion);
    },
    [onBlur]
  );

  const input = (
    <MentionsInputBase
      multiline={multiline}
      aria-label={label}
      value={value}
      onFocus={handleFocus}
      onBlur={handleBlur}
      {...rest}
    />
  );

  if (multiline) {
    return (
      <>
        <Grid
          className={classes.gridContainer}
          style={{
            border: isOverLimit
              ? `1px solid ${theme.palette.error.main}`
              : isFocused
              ? `2px solid ${theme.palette.primary.main}`
              : `1px solid ${
                  isHovered
                    ? theme.palette.text.primary
                    : theme.palette.background.outline
                }`,
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <InputLabel
            className={classes.inputLabel}
            style={{
              color: isOverLimit
                ? theme.palette.error.main
                : isFocused
                ? theme.palette.primary.main
                : theme.palette.text.disabled,
            }}
          >
            {label}
          </InputLabel>

          {input}
        </Grid>
        <InfoSection value={value} maxLength={maxLength} label={label} />
      </>
    );
  }

  return (
    <>
      <Grid
        container
        alignItems='flex-end'
        wrap='nowrap'
        className={containerClassName}
      >
        <Grid item xs className={classes.inlineInput}>
          {input}
        </Grid>
        {!!endAdornment && <Grid item>{endAdornment}</Grid>}
      </Grid>
      {!!maxLength && !!value && (
        <Box paddingX={1}>
          <InfoSection value={value} maxLength={maxLength} label={label} />
        </Box>
      )}
    </>
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  gridContainer: {
    borderRadius: 4,
    padding: 0,
    position: 'relative',
  },
  inputLabel: {
    position: 'absolute',
    top: -8,
    left: 14,
    fontSize: '0.75rem',
    backgroundColor: theme.palette.background.paper,
    padding: '0 4px',
  },
  inlineInput: {
    ...theme.typography.body2,
    overflow: 'visible',
  },
}));
